import { Autocomplete, Box, type AutocompleteProps, type TextFieldProps } from '@mui/material';
import * as React from 'react';
import { Loading } from '../../../uikit';
import TextFieldWithTooltip from '../TextFieldWithTooltip/TextFieldWithTooltip';

export interface TemplateOption {
  uuid: string;
  name: string;
}

export type TemplateDisplay =
  | TemplateOption
  | {
      uuid: null;
      name: string;
    };

type TemplateAutocompleteProps = AutocompleteProps<TemplateDisplay, false, false, false>;

type DerivedProps = Omit<TemplateAutocompleteProps, 'options' | 'renderInput' | 'onChange' | 'autoFocus'> &
  Pick<TextFieldProps, 'label' | 'error' | 'helperText' | 'required' | 'autoFocus'>;

export type TemplateInputProps = DerivedProps & {
  templates: readonly TemplateOption[];
  tooltip?: string;
  onChange?: (option: TemplateDisplay | null) => void;
};

const BLANK_TEMPLATE_OPTION: TemplateDisplay = {
  uuid: null,
  name: 'Blank Template',
};

const defaultGetOptionLabel = (option: TemplateDisplay) => option.name;

const defaultIsOptionEqualToValue = (option: TemplateDisplay, value: TemplateDisplay) => option.uuid === value.uuid;

const defaultRenderOption: NonNullable<TemplateAutocompleteProps['renderOption']> = (props, option) => (
  <Box
    component="li"
    {...props}
    sx={{
      display: 'flex',
      alignItems: 'center',
      py: 0.5,
    }}
  >
    {option.name}
  </Box>
);

export default function TemplateInput({
  templates,
  value,
  onChange,
  label = 'Templates',
  error,
  helperText,
  required,
  autoFocus,
  tooltip,
  loading = false,
  size = 'small',
  getOptionLabel = defaultGetOptionLabel,
  isOptionEqualToValue = defaultIsOptionEqualToValue,
  renderOption = defaultRenderOption,
  ...autocompleteProps
}: TemplateInputProps) {
  const options = React.useMemo<TemplateDisplay[]>(() => [BLANK_TEMPLATE_OPTION, ...templates], [templates]);

  const handleChange: TemplateAutocompleteProps['onChange'] = (_event, selectedOption) => {
    onChange?.(selectedOption);
  };

  const renderInput: TemplateAutocompleteProps['renderInput'] = (params) => {
    const { ref: inputRef, ...inputProps } = params.inputProps;

    const textFieldProps: TextFieldProps = {
      ...params,
      label,
      size,
      error,
      helperText,
      required,
      autoFocus,
      slotProps: {
        input: {
          ...params.InputProps,
          inputRef,
          endAdornment: (
            <>
              {loading && <Loading size={18} />}
              {params.InputProps.endAdornment}
            </>
          ),
        },
        htmlInput: {
          ...inputProps,
          maxLength: 100,
          'aria-label': typeof label === 'string' ? label : undefined,
        },
      },
    };

    return <TextFieldWithTooltip {...textFieldProps} tooltip={tooltip} />;
  };

  return (
    <Autocomplete<TemplateDisplay, false, false, false>
      {...autocompleteProps}
      options={options}
      value={value}
      onChange={handleChange}
      loading={loading}
      size={size}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      renderOption={renderOption}
      renderInput={renderInput}
    />
  );
}
